import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import type { Brand } from '../interfaces/brand/brand.interface';
import type { Project } from '../interfaces/project';
import { BrandService } from '../services/brand.service';
import { ProjectService } from '../services/project.service';

/**
 * Answers "which brand owns this project?" for a bare project uuid.
 *
 * A Recent activity row names its project (`projectUuid`) but no workspace and no brand,
 * and unlike the org entities a project is NOT uuid-pinned to a drive directory — its
 * drive root is a separate `driveDirectoryUuid` — so the drive hierarchy cannot be walked
 * from the id the row carries. The two hops are therefore:
 *
 *  1. project → `workspaceId`: in memory for the project already open, else one read.
 *  2. workspace → brand: always in memory, from the `Brand.workspaces` id lists
 *     {@link BrandService.loadUserBrands} holds (see {@link BrandService.brandForWorkspace}).
 *
 * So a click costs one request at most, once per project per session, and nothing for the
 * project on screen. Null means "cannot tell" — an unreadable project, a workspace in no
 * loaded brand, or a brand list that has not arrived yet — and callers must treat it as
 * "do not switch" rather than "no brand".
 */
@Injectable({ providedIn: 'root' })
export class ProjectBrandResolver {
  private readonly projects: ProjectService = inject(ProjectService);
  private readonly brands: BrandService = inject(BrandService);

  /** A project never changes brand mid-session, so a resolved answer is kept. */
  private readonly resolved: Map<string, string | null> = new Map<string, string | null>();
  private readonly inFlight: Map<string, Promise<string | null>> = new Map<string, Promise<string | null>>();

  public async resolve(projectUuid: string): Promise<string | null> {
    const workspaceUuid: string | null = await this.workspaceOf(projectUuid);
    if (workspaceUuid === null) return null;
    // Deliberately not cached with the workspace: `brands()` may still be loading, and a
    // null from that is a timing answer rather than a fact about the project.
    return this.brands.brandForWorkspace(workspaceUuid)?.id ?? null;
  }

  private workspaceOf(projectUuid: string): Promise<string | null> {
    // The project on screen is already loaded — the short-circuit that keeps a click on
    // the current project free.
    const open: Project | null = this.projects.project;
    if (open?.id === projectUuid && open.workspaceId) {
      return Promise.resolve(open.workspaceId);
    }

    const cached: string | null | undefined = this.resolved.get(projectUuid);
    if (cached !== undefined) return Promise.resolve(cached);

    const pending: Promise<string | null> | undefined = this.inFlight.get(projectUuid);
    if (pending !== undefined) return pending;

    const read: Promise<string | null> = this.readWorkspace(projectUuid).then(
      (workspaceUuid: string | null) => {
        this.resolved.set(projectUuid, workspaceUuid);
        this.inFlight.delete(projectUuid);
        return workspaceUuid;
      },
      () => {
        // Not cached: a transient failure must not settle this project for the session.
        this.inFlight.delete(projectUuid);
        return null;
      }
    );
    this.inFlight.set(projectUuid, read);
    return read;
  }

  private async readWorkspace(projectUuid: string): Promise<string | null> {
    const project: Project | null = await firstValueFrom(this.projects.getSingleProject(projectUuid));
    return project?.workspaceId ?? null;
  }

  /** Exposed for the caller that needs the brand object, not just its id. */
  public brandFor(brandId: string): Brand | null {
    return this.brands.brands().find((brand: Brand) => brand.id === brandId) ?? null;
  }
}
